import { Button, TextField, Tooltip } from "@shopify/polaris"
import React, { useEffect, useState } from "react"
import useDebounce from "../hooks/useDebounce.js"
import { useCheckIfEmailIsRegistered } from "../hooks/useFirestoreUserHooks.js"
import {
	createUserWithEmailAndPassword,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
} from "firebase/auth"
import { useTranslation } from "react-i18next"
import { usePostHog } from "posthog-js/react"
import { completeAutostoreOnboarding } from "../pages/Login.jsx"

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const EmailAndPasswordForm = ({ firebaseAuth, emailOverride }) => {
	const posthog = usePostHog()
	const { t } = useTranslation()
	const checkIfEmailIsRegistered = useCheckIfEmailIsRegistered()

	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [passwordConfirm, setPasswordConfirm] = useState("")
	const [showPasswordField, setShowPasswordField] = useState(false)

	const [
		loadingCheckIfEmailIsRegistered,
		setLoadingCheckIfEmailIsRegistered,
	] = useState(false)
	const [emailIsRegistered, setEmailIsRegistered] = useState(false)

	const [loadingSignInOrSignUp, setLoadingSignInOrSignUp] = useState(false)
	const [signInOrSignUpError, setSignInOrSignUpError] = useState(null)

	const [hasClickedResetPassword, setHasClickedResetPassword] =
		useState(false)

	const debouncedEmail = useDebounce(email, 500)

	const signUp = async () => {
		setLoadingSignInOrSignUp(true)
		setSignInOrSignUpError(null)
		try {
			await createUserWithEmailAndPassword(
				firebaseAuth,
				email,
				password,
			).then(async (res) => {
				const done = await completeAutostoreOnboarding()
				if (done) return

				if (res.user.uid) {
					if (window.tolt?.signup) {
						window.tolt.signup(email)
					}

					posthog?.capture(
						"user_logged_in_v2",
						{ method: "email_and_password" },
						{ transport: "sendBeacon" },
					)
				}
			})
		} catch (error) {
			console.error(error)
			if (error?.message?.includes("auth/email-already-in-use")) {
				setSignInOrSignUpError(
					t(
						"login.that_email_is_already_in_use_sign_in_with_your_google_account_above",
					),
				)
			} else {
				setSignInOrSignUpError(error.message)
			}
		}
		setLoadingSignInOrSignUp(false)
	}

	const logIn = async () => {
		setLoadingSignInOrSignUp(true)
		setSignInOrSignUpError(null)
		try {
			await signInWithEmailAndPassword(
				firebaseAuth,
				email,
				password,
			).then((res) => {
				if (res.user.uid) {
					posthog?.capture(
						"user_logged_in_v2",
						{ method: "email_and_password" },
						{ transport: "sendBeacon" },
					)
				}
			})
		} catch (error) {
			console.error(error)
			if (
				error?.message?.includes("auth/invalid-login-credentials") ||
				error?.message?.includes("auth/invalid-credential")
			) {
				setSignInOrSignUpError(
					t(
						"login.invalid_password_if_you_forgot_your_password_click_forgot_password_above",
					),
				)
			} else {
				setSignInOrSignUpError(error.message)
			}
		}
		setLoadingSignInOrSignUp(false)
	}

	const forgotPassword = async () => {
		setHasClickedResetPassword(true)
		await sendPasswordResetEmail(firebaseAuth, email)
	}

	useEffect(async () => {
		if (email) {
			setLoadingCheckIfEmailIsRegistered(true)

			if (emailRegex.test(email)) {
				setShowPasswordField(true)
			}
		} else {
			setLoadingCheckIfEmailIsRegistered(false)
			setShowPasswordField(false)
		}
	}, [email])

	useEffect(async () => {
		if (debouncedEmail && emailRegex.test(debouncedEmail)) {
			const { emailIsRegistered } =
				await checkIfEmailIsRegistered(debouncedEmail)
			setEmailIsRegistered(emailIsRegistered)
			setLoadingCheckIfEmailIsRegistered(false)
		} else {
			setEmailIsRegistered(false)
			setLoadingCheckIfEmailIsRegistered(false)
		}
	}, [debouncedEmail])

	useEffect(() => {
		if (emailOverride) {
			setEmail(emailOverride)
		}
	}, [emailOverride])

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: "1rem",
				position: "relative",
			}}
		>
			<div>
				<div
					style={{ display: "flex", justifyContent: "space-between" }}
				>
					<span style={{ fontSize: "0.7rem", color: "#333" }}>
						{t("login.email")}
					</span>
					<Tooltip
						content={
							<div style={{ width: "200px", padding: "0.5rem" }}>
								<span
									style={{
										fontSize: "0.75rem",
										color: "#333",
									}}
								>
									{t(
										"login.if_you_previously_signed_in_using_email_link_please_enter_the_same_email_address_you_entered_before_then_click",
									)}{" "}
									<b>{t("login.forgot_password")}</b>{" "}
									{t("login.below_to_obtain_a_password")}
								</span>
							</div>
						}
					>
						<div style={{ height: "1.3rem", color: "#777" }}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="0.777rem"
								height="0.777rem"
								viewBox="0 0 14 16"
							>
								<path
									fillRule="evenodd"
									d="M6.3 5.69a.942.942 0 0 1-.28-.7c0-.28.09-.52.28-.7c.19-.18.42-.28.7-.28c.28 0 .52.09.7.28c.18.19.28.42.28.7c0 .28-.09.52-.28.7a1 1 0 0 1-.7.3c-.28 0-.52-.11-.7-.3zM8 7.99c-.02-.25-.11-.48-.31-.69c-.2-.19-.42-.3-.69-.31H6c-.27.02-.48.13-.69.31c-.2.2-.3.44-.31.69h1v3c.02.27.11.5.31.69c.2.2.42.31.69.31h1c.27 0 .48-.11.69-.31c.2-.19.3-.42.31-.69H8V7.98v.01zM7 2.3c-3.14 0-5.7 2.54-5.7 5.68c0 3.14 2.56 5.7 5.7 5.7s5.7-2.55 5.7-5.7c0-3.15-2.56-5.69-5.7-5.69v.01zM7 .98c3.86 0 7 3.14 7 7s-3.14 7-7 7s-7-3.12-7-7s3.14-7 7-7z"
									fill="currentColor"
								></path>
							</svg>
						</div>
					</Tooltip>
				</div>

				<TextField
					placeholder="user@email.com"
					type="email"
					autoComplete="off"
					onChange={(change) => setEmail(change)}
					value={email}
					connectedRight={
						loadingCheckIfEmailIsRegistered ? (
							<div
								style={{
									height: "100%",
									display: "flex",
									alignItems: "center",
									color:
										email === debouncedEmail
											? "#333"
											: "#aaa",
								}}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="1rem"
									height="1rem"
									viewBox="0 0 24 24"
								>
									<g>
										<rect
											width="2"
											height="5"
											x="11"
											y="1"
											fill="currentColor"
											opacity=".14"
										></rect>
										<rect
											width="2"
											height="5"
											x="11"
											y="1"
											fill="currentColor"
											opacity=".29"
											transform="rotate(30 12 12)"
										></rect>
										<rect
											width="2"
											height="5"
											x="11"
											y="1"
											fill="currentColor"
											opacity=".43"
											transform="rotate(60 12 12)"
										></rect>
										<rect
											width="2"
											height="5"
											x="11"
											y="1"
											fill="currentColor"
											opacity=".57"
											transform="rotate(90 12 12)"
										></rect>
										<rect
											width="2"
											height="5"
											x="11"
											y="1"
											fill="currentColor"
											opacity=".71"
											transform="rotate(120 12 12)"
										></rect>
										<rect
											width="2"
											height="5"
											x="11"
											y="1"
											fill="currentColor"
											opacity=".86"
											transform="rotate(150 12 12)"
										></rect>
										<rect
											width="2"
											height="5"
											x="11"
											y="1"
											fill="currentColor"
											transform="rotate(180 12 12)"
										></rect>
										<animateTransform
											attributeName="transform"
											calcMode="discrete"
											dur="0.75s"
											repeatCount="indefinite"
											type="rotate"
											values="0 12 12;30 12 12;60 12 12;90 12 12;120 12 12;150 12 12;180 12 12;210 12 12;240 12 12;270 12 12;300 12 12;330 12 12;360 12 12"
										></animateTransform>
									</g>
								</svg>
							</div>
						) : (
							""
						)
					}
				/>
			</div>

			{loadingCheckIfEmailIsRegistered ? (
				""
			) : showPasswordField && emailIsRegistered ? (
				<TextField
					label={t("login.password")}
					type="password"
					onChange={(change) => setPassword(change)}
					value={password}
				/>
			) : showPasswordField ? (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "0.5rem",
					}}
				>
					<TextField
						label={t("login.password")}
						type="password"
						onChange={(change) => setPassword(change)}
						value={password}
					/>
					<TextField
						label={t("login.repeat_password")}
						type="password"
						onChange={(change) => setPasswordConfirm(change)}
						value={passwordConfirm}
					/>
				</div>
			) : (
				""
			)}

			{!loadingCheckIfEmailIsRegistered && showPasswordField && (
				<Button
					onClick={emailIsRegistered ? logIn : signUp}
					loading={
						loadingCheckIfEmailIsRegistered || loadingSignInOrSignUp
					}
				>
					{emailIsRegistered ? t("login.log_in") : t("login.sign_up")}
				</Button>
			)}

			{!loadingCheckIfEmailIsRegistered &&
				showPasswordField &&
				emailIsRegistered &&
				!hasClickedResetPassword && (
					<Button onClick={forgotPassword} plain>
						{t("login.forgot_password")}
					</Button>
				)}

			{!loadingCheckIfEmailIsRegistered &&
				showPasswordField &&
				emailIsRegistered &&
				hasClickedResetPassword && (
					<span
						style={{
							fontSize: "0.777rem",
							margin: "auto",
							color: "#0f850f",
						}}
					>
						{t("login.password_reset_email_sent")}
					</span>
				)}

			{signInOrSignUpError && (
				<div style={{ color: "red", fontSize: "0.8rem" }}>
					{signInOrSignUpError}
				</div>
			)}
		</div>
	)
}
